import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ElevatedButton } from '@producer/ui-kit';

import { CrewList } from '../../features/crew/components/crew-list';
import { CrewFilters } from '../../features/crew/components/crew-filters';
import { CrewGroupingDropdown } from '../../features/crew/components/crew-grouping-dropdown';
import { CrewExportMenu } from '../../features/crew/components/crew-export-menu';
import { CrewMemberModal } from '../../features/crew/components/crew-member.modal';
import { AssignCrewMemberModal } from '../../features/crew/components/assign-crew-member.modal';
import {
  deleteCrewMember,
  fetchCrew,
  saveCrewMemberField,
} from '../../features/crew/stores/crew.slice';
import { catchError } from '../../core/catch-error';
import { filterCrew, type CrewFilter } from '../../features/crew/lib/filter-crew';
import { useCurrentProject } from '../../hooks/use-current-project';
import { uiStore } from '../../core/stores/ui-store';
import { ToolBar, Tools } from '../../components/toolbar/toolbar';
import { Header } from './project.page';

import type { CrewGroupBy } from '../../features/crew/components/crew-list';
import type { CrewMember } from '../../app/entities/crew-member';

import styles from './project-crew.route.module.scss';

export const ProjectCrewRoute = observer(() => {
  const project = useCurrentProject()!;
  // `undefined` means the create modal is closed; an object (possibly empty) means it is open,
  // pre-filled with whatever the caller passed — nothing from the header's own "Add crew member"
  // button, or `{ [groupBy]: value }` from a group header's plus button.
  const [createDefaults, setCreateDefaults] = useState<Record<string, string> | undefined>(
    undefined,
  );
  const [editing, setEditing] = useState<CrewMember | null>(null);
  const [assigning, setAssigning] = useState<CrewMember | null>(null);
  const [filter, setFilter] = useState<CrewFilter>({});
  const [groupBy, setGroupBy] = useState<CrewGroupBy>(null);

  // The "Ungrouped" bucket's value is `''` — pre-filling `{ department: '' }` would be worse than
  // no default at all, so an empty group value (or no active `groupBy`) opens the form blank.
  const handleAddToGroup = (value: string) => {
    setCreateDefaults(groupBy && value ? { [groupBy]: value } : {});
  };

  useEffect(() => {
    uiStore.currentLocation = 'project.crew';

    return () => {
      uiStore.currentLocation = '';
    };
  }, []);

  // Crew is not part of the project bootstrap payload, so the route fetches its own data — the
  // same shape as Locations and Characters.
  useEffect(() => {
    fetchCrew(project._id);
  }, [project._id]);

  // The route owns the filter and hands `filterCrew`'s output to the presentational, controlled
  // `<CrewList>`. Keeping the filter here — rather than inside the list — is what lets this same
  // object also feed `<CrewExportMenu>` below, so the export can never disagree with what is on
  // screen.
  const visible = filterCrew(project.crewMembers, filter);

  return (
    <>
      <Header>
        <div>Crew</div>

        <div className={styles['actions']}>
          <CrewExportMenu projectId={project._id} filter={filter} />
          <ElevatedButton text="Add crew member" onClick={() => setCreateDefaults({})} />
        </div>
      </Header>

      <div className={styles['content']}>
        <ToolBar>
          <CrewFilters
            value={filter}
            onChange={setFilter}
            departments={project.crewDepartments}
            teams={project.crewTeams}
          />

          <div />

          <Tools>
            <CrewGroupingDropdown value={groupBy} onChange={setGroupBy} />
          </Tools>
        </ToolBar>

        <div className={styles['container']}>
          {/* This screen owns crew records, so it offers the row actions and inline editing.
              `CrewList` itself stays presentational — it renders the menu, confirms the delete
              and commits one field at a time, and every mutation happens here. */}
          <CrewList
            members={visible}
            groupBy={groupBy}
            onEdit={setEditing}
            onDelete={(member) =>
              deleteCrewMember(member).catch((e) =>
                catchError(e, 'Could not delete this crew member. Try again.'),
              )
            }
            onAssign={setAssigning}
            onAddToGroup={handleAddToGroup}
            onCreate={() => setCreateDefaults({})}
            onUpdate={(member, update) =>
              saveCrewMemberField(project, member, update).catch((e) =>
                catchError(e, 'Could not save this change. Try again.'),
              )
            }
            departments={project.crewDepartments}
            teams={project.crewTeams}
          />
        </div>
      </div>

      {createDefaults && (
        <CrewMemberModal
          project={project}
          defaults={createDefaults}
          onCancel={() => setCreateDefaults(undefined)}
          onClose={() => setCreateDefaults(undefined)}
        />
      )}

      {editing && (
        <CrewMemberModal
          project={project}
          member={editing}
          onCancel={() => setEditing(null)}
          onClose={() => setEditing(null)}
        />
      )}

      {assigning && (
        <AssignCrewMemberModal
          project={project}
          member={assigning}
          onCancel={() => setAssigning(null)}
          onClose={() => setAssigning(null)}
        />
      )}
    </>
  );
});
